import {Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';

import {BaseChartDirective} from 'ng2-charts';
import {ChartOptions} from 'chart.js';
import {de} from 'date-fns/locale';
import {AverageEnergyResponse} from '../../../../api/models/average-energy-response';
import {DateTimeValue} from '../../../../api/models/date-time-value';
import {BaseChartComponent, EnergyType} from '../energy-chart/base-chart.component';
import {
  buildDebugStatusTables,
  DebugBatteryMode,
  DebugNodeTable,
  DebugValueMode,
  formatDebugStatusValue,
  getDebugStatusEntries,
  normalizeDebugStatusValue,
} from './debug-status-table.helper';

@Component({
  selector: 'app-energy-chart-debug-chart',
  templateUrl: './energy-chart-debug-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BaseChartDirective],
})
export class EnergyChartDebugChartComponent extends BaseChartComponent implements OnChanges {
  private get locationDateTimeFormatter(): Intl.DateTimeFormat {
    return new Intl.DateTimeFormat('de-AT', {
      timeZone: this.timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }
  @Input() type: EnergyType = 'production';
  @Input() batteryMode: DebugBatteryMode = 'power';
  @Input() date: Date = new Date();
  @Input() data: AverageEnergyResponse | undefined;

  override interval: 'day' = 'day';
  debugTables: DebugNodeTable[] = [];

  lineChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...this.baseTooltipOptions,
      legend: {
        position: 'bottom',
        display: true,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          padding: 12,
          font: {
            size: 12,
          },
        },
        border: {
          display: false,
        },
      },
      x: {
        type: 'time',
        time: {
          unit: 'hour',
          tooltipFormat: 'HH:mm',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
          },
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        ticks: {
          minRotation: this.isMobile() ? 90 : 0,
          maxRotation: this.isMobile() ? 90 : 0,
          autoSkip: true,
          maxTicksLimit: this.isMobile() ? 12 : 24,
          padding: this.isMobile() ? 4 : 8,
          font: {
            size: this.isMobile() ? 10 : 12,
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['type'] || changes['batteryMode'] || changes['date']) {
      this.initializeChartData();
    }
  }

  private initializeChartData(): void {
    if (!this.data) {
      this.debugTables = [];
      this.setChart([], []);
      return;
    }

    const datasets: Record<string, any>[] = [];
    const allLabels: Date[][] = [];
    const nodeTables = buildDebugStatusTables(this.data, this.type, this.batteryMode);
    let colorIndex = 0;

    getDebugStatusEntries(this.data, this.type, this.batteryMode).forEach((entry) => {
      const deviceTimeValues = (entry.status?.deviceTimeValues ?? {}) as Record<string, DateTimeValue[]>;
      const deviceNames = (entry.status?.deviceNames ?? {}) as Record<string, string>;
      const nodeIds = Array.from(new Set([...Object.keys(deviceNames), ...Object.keys(deviceTimeValues)]));

      if (nodeIds.length === 0) {
        return;
      }

      nodeIds.forEach((deviceKey) => {
        const rawTimeValues = deviceTimeValues[deviceKey];
        const timeValues = Array.isArray(rawTimeValues) ? rawTimeValues : [];
        const deviceName = deviceNames[deviceKey] || deviceKey;
        const tableLabel = entry.prefix ? `${entry.prefix}: ${deviceName}` : deviceName;

        if (timeValues.length === 0) {
          return;
        }

        const completed = this.completeData(
          timeValues,
          'day',
          this.date,
          'zero',
          entry.status?.timeBucket,
        ).map((item) => ({
          ...item,
          value: normalizeDebugStatusValue(item.value, entry.mode),
        }));

        if (!completed.some((item) => typeof item.value === 'number' && item.value !== 0)) {
          return;
        }

        const labels = this.toLabels(completed);
        allLabels.push(labels);

        const color = this.getDebugColor(this.type, colorIndex++, entry.mode);
        datasets.push({
          label: tableLabel,
          data: completed.map((item) => item.value),
          backgroundColor: color,
          borderColor: color,
          showLine: false,
          pointRadius: 1,
          pointHoverRadius: 5,
          borderWidth: 0,
          tension: 0,
          spanGaps: false,
        });
      });
    });

    this.debugTables = nodeTables;
    this.setChart(this.processLabels(allLabels), datasets);
  }

  private setChart(labels: Date[], datasets: Record<string, any>[]): void {
    this.chartType.set('line');
    this.chartOptions.set(this.lineChartOptions);
    this.chartData.set({
      labels,
      datasets,
    });

    if (this.chart) {
      this.chart.update();
    }
  }

  private toLabels(timeValues: DateTimeValue[]): Date[] {
    const labels: Date[] = [];
    timeValues.forEach((item) => {
      const date = new Date(item.read ?? '');
      if (!isNaN(date.getTime())) {
        labels.push(date);
      }
    });
    return labels;
  }

  private getDebugColor(type: EnergyType, index: number, mode: DebugValueMode): string {
    if (mode === 'negative') {
      return '#9ca3af';
    }

    const root = document.documentElement;
    const colors: Record<EnergyType, string[]> = {
      production: [getComputedStyle(root).getPropertyValue('--color-production-yellow').trim(), '#FDD835', '#F9A825', '#F57F17'],
      consumption: [getComputedStyle(root).getPropertyValue('--color-consumption-blue').trim(), '#2196F3', '#1565C0', '#0D47A1'],
      battery: [getComputedStyle(root).getPropertyValue('--color-battery-green').trim(), '#66BB6A', '#2E7D32', '#1B5E20'],
      grid: ['#333333', '#4B5563', '#6B7280', '#9CA3AF'],
    };

    const palette = colors[type] || ['#333333'];
    return palette[index % palette.length];
  }

  formatDebugValue(value: number | null | undefined): string {
    return formatDebugStatusValue(value);
  }

  formatTimestamp(timestamp: string | null | undefined): string {
    if (!timestamp) {
      return '';
    }

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return timestamp;
    }

    return this.locationDateTimeFormatter.format(date);
  }
}
